import React, { useRef, useEffect, useState, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import moment from 'moment';
import api from '../api/api';
import { useHistory } from 'react-router-dom';

import '../layout/sass/_pickListSugestoes.scss';
import '../layout/sass/_responsiveTable.scss';

// Configura o locale para português (opcional, mas recomendado)
moment.locale('pt-br');

export const TabelaTurmaPlanejamento = ({ cursoId, instrutorId, turmaId }) => {
  const toast = useRef(null);
  const [tarefas, setTarefas] = useState([]);
  const [perfil, setPerfil] = useState({});
  const [showPerfil, setShowPerfil] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [observacao, setObservacao] = useState('');
  const [loadingExecucao, setLoadingExecucao] = useState(false);

  const [showExecDialog, setShowExecDialog] = useState(false);
  const [showUndoDialog, setShowUndoDialog] = useState(false);

  const planejamentoDescricao =
    tarefas.content?.[0]?.planejamentoDescricao || 'Planejamento Atual';

  const planejamentoId = tarefas.content?.[0]?.planejamentoId;

  const history = useHistory();

  const hasPlanejamento = tarefas.content?.[0]?.planejamentoDescricao;

  const handleEditClick = (data) => {
    history.push(`/planejamentos/edit/${planejamentoId}`);
  };

  const header = (
    <div className="p-grid p-col-12 p-m-0 p-p-0 p-jc-between">
      <Tooltip
        target=".custom-target-tooltip"
        content={planejamentoDescricao}
        position="top"
        style={{
          maxWidth: '700px',
        }}
      />

      <h2 className="custom-target-tooltip p-my-0">
        {planejamentoDescricao.length > 80
          ? `${planejamentoDescricao.slice(0, 80)}...`
          : planejamentoDescricao}
      </h2>

      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-mr-2 edit-btn"
        onClick={() => handleEditClick()}
        disabled={!hasPlanejamento}
      />
    </div>
  );

  // === BODY TEMPLATES COM MOMENT ===

  const codBodyTemplate = (rowData) => {
    return (
      <p style={{ textAlign: 'left', margin: 0 }}>
        <span className="p-column-title">Código</span>
        <span className="table-data">{rowData.id}</span>
      </p>
    );
  };

  const tarefaDescricaoBodyTemplate = (rowData) => (
    <p style={{ textAlign: 'left', margin: 0 }}>
      <span className="p-column-title">Descrição</span>
      <span className="table-data">{rowData.tarefa?.descricao || '—'}</span>
    </p>
  );

  const periodoLetivoBodyTemplate = (rowData) => (
    <p style={{ textAlign: 'center', margin: 0 }}>
      <span className="p-column-title">Período Letivo</span>
      <span className="table-data">
        {rowData.periodoLetivoDescricao || '—'}
      </span>
    </p>
  );

  const dataLimiteBodyTemplate = (rowData) => {
    const data = rowData.dataLimite;
    const formatted = data ? moment(data).format('DD/MM/YYYY') : null;

    return (
      <p style={{ textAlign: 'center', margin: 0 }}>
        <span className="p-column-title">Data Limite</span>
        <span className="table-data">{formatted || 'NÃO CONCLUÍDO'}</span>
      </p>
    );
  };

  const dataExecucaoBodyTemplate = (rowData) => {
    const isExecutado = rowData.execucaoPlanejamentoId !== null;
    const data = rowData.execucaoPlanejamentoUpdatedAt;
    const formatted = data ? moment(data).format('DD/MM/YYYY') : null;

    return (
      <p style={{ textAlign: 'center', margin: 0 }}>
        <span className="p-column-title">Data de Execução</span>
        <br />
        <span className="table-data">
          {isExecutado ? (
            formatted || '—'
          ) : (
            <span className="text-muted">Não Executado</span>
          )}
        </span>
      </p>
    );
  };

  const obsTarefaBodyTemplate = (rowData) => (
    <p style={{ textAlign: 'center', margin: 0 }}>
      <span className="table-data">{rowData.obs || '—'}</span>
    </p>
  );

  // === CARREGAMENTO DE DADOS ===

  const getPlanejamentoTarefas = useCallback(async () => {
    if (!cursoId || !instrutorId) return;

    try {
      const response = await api.get(
        `planejamento/curso/${cursoId}/instrutor/${instrutorId}/turma/${turmaId}/tarefas`,
      );

      setTarefas(response.data || { content: [] });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: 'Falha ao carregar tarefas.',
        life: 3000,
      });
    }
  }, [cursoId, instrutorId]);

  useEffect(() => {
    getPlanejamentoTarefas();
  }, [getPlanejamentoTarefas]);

  // === FUNÇÕES AUXILIARES ===

  const handlePerfilClick = (perfilData) => {
    setPerfil(perfilData);
    setShowPerfil(true);
  };

  const formatCpf = (cpf) => {
    return cpf
      ? cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
      : '—';
  };

  const fecharDialogs = () => {
    setShowExecDialog(false);
    setShowUndoDialog(false);
    setObservacao('');
    setSelectedRow(null);
  };

  const handleToggleCheckbox = (rowData, isExecutado) => {
    setSelectedRow(rowData);

    if (isExecutado) {
      setShowUndoDialog(true);
    } else {
      setShowExecDialog(true);
    }
  };

  const executarTarefa = async (obsTexto) => {
    if (!selectedRow) return;

    setLoadingExecucao(true);

    try {
      const payload = {
        planejamentoTarefa: { id: selectedRow.id },
        turma: { id: turmaId },
        obs: obsTexto,
        dataLimiteExecucao: selectedRow.dataLimite,
      };

      await api.post('/execucao-planejamento', payload);

      getPlanejamentoTarefas();
      fecharDialogs();
      toast.current?.show({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Tarefa executada com sucesso.',
        life: 3000,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingExecucao(false);
    }
  };

  const desfazerExecucao = async () => {
    if (!selectedRow?.execucaoPlanejamentoId) return;

    setLoadingExecucao(true);

    try {
      await api.delete(
        `/execucao-planejamento/${selectedRow.execucaoPlanejamentoId}`,
      );

      getPlanejamentoTarefas();
      fecharDialogs();
      toast.current?.show({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Execução desfeita',
        life: 3000,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingExecucao(false);
    }
  };

  const StatusCheckbox = ({ rowData, onToggle }) => {
    const isExecutado = rowData.execucaoPlanejamentoId !== null;

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle(rowData, isExecutado);
    };
    return (
      <div style={{ textAlign: 'center' }}>
        <div
          style={{ textAlign: 'center', cursor: 'pointer' }}
          onClick={handleClick}
        >
          <Checkbox
            checked={isExecutado}
            tooltip={isExecutado ? 'Concluído' : 'Pendente'}
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      </div>
    );
  };

  // === RENDER ===

  return (
    <>
      <Toast ref={toast} position="bottom-right" />

      <Dialog
        header="Perfil"
        visible={showPerfil}
        onHide={() => setShowPerfil(false)}
        breakpoints={{ '960px': '75vw', '640px': '100vw' }}
        style={{ width: '50vw' }}
      >
        <div className="p-d-flex p-flex-column">
          <div className="p-grid">
            <div className="p-col-12">
              <b>Nome:</b> {perfil.nome || '—'}
            </div>
            <div className="p-col-12">
              <b>CPF:</b> {formatCpf(perfil.cpf)}
            </div>
            <div className="p-col-12">
              <b>Email:</b> {perfil.email || '—'}
            </div>
            <div className="p-col-12">
              <b>Data de Nascimento:</b>{' '}
              {perfil.dataNascimento
                ? moment(perfil.dataNascimento).format('DD/MM/YYYY')
                : '—'}
            </div>
            <div className="p-col-12">
              <b>Deficiência:</b> {perfil.deficiencia || '—'}
            </div>
            <div className="p-col-12">
              <b>Responsável:</b> {perfil.responsavel || '—'}
            </div>
            <div className="p-col-12">
              <b>Telefone:</b> {perfil.telefone || '—'}
            </div>
            <div className="p-col-12">
              <b>Celular:</b> {perfil.celular || '—'}
            </div>
          </div>
        </div>
      </Dialog>

      <div className="card p-col-12 datatable-responsive-demo">
        <DataTable
          value={tarefas.content || []}
          header={header}
          emptyMessage="Nenhuma tarefa encontrada"
          className="p-datatable-responsive-demo"
          dataKey="id"
          rows={50}
        >
          <Column
            field="id"
            header="Código"
            body={codBodyTemplate}
            style={{ width: '5%' }}
          />
          <Column
            field="tarefa.descricao"
            header="Descrição"
            body={tarefaDescricaoBodyTemplate}
            style={{ width: '30%' }}
          />
          <Column
            field="periodoLetivo"
            header="Período Letivo"
            body={periodoLetivoBodyTemplate}
            style={{ width: '15%', textAlign: 'center' }}
          />
          <Column
            field="dataLimite"
            header="Data Limite"
            body={dataLimiteBodyTemplate}
            style={{ width: '12%', textAlign: 'center' }}
          />
          <Column
            field="execucaoPlanejamentoId"
            header="Executado?"
            body={(rowData) => (
              <StatusCheckbox
                rowData={rowData}
                onToggle={handleToggleCheckbox}
              />
            )}
            style={{ width: '100px', textAlign: 'center' }}
          />
          <Column
            field="dataExecucao"
            header="Data Execução"
            body={dataExecucaoBodyTemplate}
            style={{ width: '15%' }}
          />
          <Column
            field="obs"
            header="Observação"
            body={obsTarefaBodyTemplate}
            style={{ width: '15%', textAlign: 'center' }}
          />
        </DataTable>
      </div>

      <Dialog
        header="Deseja adicionar alguma observação?"
        visible={showExecDialog}
        onHide={fecharDialogs}
        style={{ width: '50vw' }}
      >
        <div className="p-d-flex p-flex-column">
          <div className="p-grid">
            <div className="p-col-12">
              <p>Observação:</p>{' '}
            </div>
            <div className="p-col-12">
              <InputTextarea
                className="w-full"
                rows={4}
                autoResize
                style={{ width: '100%' }}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
              />
            </div>
            <div className="p-col-12 p-d-flex p-jc-end p-ai-center">
              <Button
                label="Adicionar"
                icon="pi pi-check"
                loading={loadingExecucao}
                onClick={() =>
                  executarTarefa(
                    observacao?.trim() || 'Tarefa realizada com sucesso',
                  )
                }
              />
              <Button
                label="Não"
                className="p-button-secondary p-ml-2"
                disabled={loadingExecucao}
                onClick={() => executarTarefa('Tarefa realizada com sucesso')}
              />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Desfazer execução"
        visible={showUndoDialog}
        onHide={fecharDialogs}
        style={{ width: '30vw' }}
      >
        <p>Deseja realmente desfazer a execução desta tarefa?</p>

        <div className="p-d-flex p-jc-end p-mt-3">
          <Button
            label="Sim"
            icon="pi pi-times"
            className="p-button-danger"
            loading={loadingExecucao}
            onClick={desfazerExecucao}
          />

          <Button
            label="Não"
            className="p-button-secondary p-ml-2"
            disabled={loadingExecucao}
            onClick={fecharDialogs}
          />
        </div>
      </Dialog>
    </>
  );
};